import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface AccountDetailState {
    id?: number | string;
    first_name: string;
    last_name: string;
    username: string;
    email: string;
    birth_date?: string;
    role_name: string;
    role_code: string;
    role: number;
    postal_code?: string;
    phone: string;
    identity_number?: string;
    bank_id?: string | number;
    bank_account_number?: string;
    bank_account_name?: string;
    is_active: 0 | 1;
    date_joined: string;
    genre?: string;
    code: string;
    wards?: any;
    address?: string;
    classes?: number;
    class_name?: string;
    school?: number;
    school_name?: string;
    avatar_url?: string;
    wards_code?: string
}

interface AccountData {
    accountList: AccountDetailState[];
}

export interface ImportProgressSession {
    sessionId: string;
    totalTasks: number;
    createdAt: number;
    ownerUsername: string | null;
}

export type DeleteProgressSession = ImportProgressSession;

const initialState: any = {
    accountList: [],
    accountCreation: {},
    loading: false,
    success: false,
    provinceList: [],
    districtList: [],
    wardList: [],
    provinceScList: [],
    districtScList: [],
    wardScList: [],
    schoolListCount: [],
    roleList: [],
    loadingProvince: false,
    loadingDistrict: false,
    loadingWard: false,
    loadingScProvince: false,
    loadingScDistrict: false,
    loadingScWard: false,
    loadingRole: false,
    enrolledList: [],
    unenrolledList: [],
    loadingEnrollments: false,
    loadingCreateAccount: false,
    failedCreateAccount: [],
    exportFile: null,
    exportFileUrl: null,
    loadingExport: false,
    loadingDownloadTemplate: false,
    templateFile: null,
    historyLogs: [],
    loadingHistoryLogs: false,
    deleteHistoryLogs: null,
    loadingDeleteHistoryLogs: false,
    retryingTask: false,
    loadingDeleteUser: false,
    deleteUserSuccess: false,
    importProgressSession: null,
    deleteProgressSession: null,
    validationSession: null,
    downloadValidatedExcelFile: null,
    validationPreFilterFailures: [],
};

const accountSlice = createSlice({
    name: 'account',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setSuccess(state, action) {
            state.success = action.payload;
        },
        setAccountList(state, action) {
            state.accountList = action.payload;
        },
        setAccountCreation(state, action) {
            state.accountCreation = action.payload;
        },
        clearAccountData(state) {
            state.accountList = [];
            state.accountCreation = {}
        },
        setProvinceList(state, action: PayloadAction<any>) {
            state.provinceList = action.payload?.results
        },
        setDistrictList(state, action: PayloadAction<any>) {
            state.districtList = action.payload
        },
        setWardList(state, action: PayloadAction<any>) {
            state.wardList = action.payload
        },
        setProvinceScList(state, action: PayloadAction<any>) {
            state.provinceScList = action.payload?.results
        },
        setDistrictScList(state, action: PayloadAction<any>) {
            state.districtScList = action.payload
        },
        setWardScList(state, action: PayloadAction<any>) {
            state.wardScList = action.payload
        },
        setSchoolList(state, action: PayloadAction<any>) {
            state.schoolList = action.payload
        },
        setRoleList(state, action: PayloadAction<any>) {
            state.roleList = action.payload
        },
        setLoadingProvince(state, action: PayloadAction<any>) {
            state.loadingProvince = action.payload
        },
        setLoadingDistrict(state, action: PayloadAction<any>) {
            state.loadingDistrict = action.payload
        },
        setLoadingWard(state, action: PayloadAction<any>) {
            state.loadingWard = action.payload
        },
        setLoadingScProvince(state, action: PayloadAction<any>) {
            state.loadingScProvince = action.payload
        },
        setLoadingScDistrict(state, action: PayloadAction<any>) {
            state.loadingScDistrict = action.payload
        },
        setLoadingScWard(state, action: PayloadAction<any>) {
            state.loadingScWard = action.payload
        },
        setLoadingRole(state, action: PayloadAction<any>) {
            state.loadingRole = action.payload
        },
        setLoadingSchool(state, action: PayloadAction<any>) {
            state.loadingSchool = action.payload
        },
        setEnrolledList(state, action: PayloadAction<any>) {
            state.enrolledList = action.payload
        },
        setUnEnrolledList(state, action: PayloadAction<any>) {
            state.unenrolledList = action.payload
        },
        setLoadingEnrollments(state, action: PayloadAction<any>) {
            state.loadingEnrollments = action.payload
        },
        setLoadingCreateAccount(state, action: PayloadAction<any>) {
            state.loadingCreateAccount = action.payload
        },
        setFailedCreateAccount(state, action: PayloadAction<any>) {
            state.failedCreateAccount = action.payload
        },
        setExportFile(state, action: PayloadAction<any>) {
            state.exportFile = action.payload
        },
        setExportFileUrl(state, action: PayloadAction<any>) {
            state.exportFileUrl = action.payload
        },
        setLoadingExport(state, action: PayloadAction<any>) {
            state.loadingExport = action.payload
        },
        clearExportFile(state) {
            state.exportFile = null
        },
        clearExportFileUrl(state) {
            state.exportFileUrl = null
        },
        setLoadingDownloadTemplate(state, action: PayloadAction<any>) {
            state.loadingDownloadTemplate = action.payload
        },
        setTemplateFile(state, action: PayloadAction<any>) {
            state.templateFile = action.payload
        },
        clearTemplateFile(state) {
            state.templateFile = null
        },
        setHistoryLogs(state, action: PayloadAction<any>) {
            state.historyLogs = action.payload
        },
        setLoadingHistoryLogs(state, action: PayloadAction<any>) {
            state.loadingHistoryLogs = action.payload
        },
        clearHistoryLogs(state) {
            state.historyLogs = []
        },
        setDeleteHistoryLogs(state, action: PayloadAction<any>) {
            state.deleteHistoryLogs = action.payload
        },
        setLoadingDeleteHistoryLogs(state, action: PayloadAction<any>) {
            state.loadingDeleteHistoryLogs = action.payload
        },
        clearDeleteHistoryLogs(state) {
            state.deleteHistoryLogs = null
        },
        setRetryingTask(state, action: PayloadAction<boolean>) {
            state.retryingTask = action.payload
        },
        setLoadingDeleteUser(state, action: PayloadAction<boolean>) {
            state.loadingDeleteUser = action.payload;
        },
        setDeleteUserSuccess(state, action: PayloadAction<boolean>) {
            state.deleteUserSuccess = action.payload;
        },
        setImportProgressSession(state, action: PayloadAction<ImportProgressSession | null>) {
            state.importProgressSession = action.payload;
        },
        setDeleteProgressSession(state, action: PayloadAction<DeleteProgressSession | null>) {
            state.deleteProgressSession = action.payload;
        },
        setValidationSession(state, action: PayloadAction<ImportProgressSession | null>) {
            state.validationSession = action.payload;
        },
        setDownloadValidatedExcelFile(state, action: PayloadAction<any>) {
            state.downloadValidatedExcelFile = action.payload;
        },
        setValidationPreFilterFailures(state, action: PayloadAction<any[]>) {
            state.validationPreFilterFailures = action.payload;
        },
    },
});

export const {
    setLoading,
    setSuccess,
    setAccountList,
    setAccountCreation,
    clearAccountData,
    setProvinceList,
    setDistrictList,
    setWardList,
    setProvinceScList,
    setDistrictScList,
    setWardScList,
    setSchoolList,
    setRoleList,
    setLoadingProvince,
    setLoadingDistrict,
    setLoadingWard,
    setLoadingScProvince,
    setLoadingScDistrict,
    setLoadingScWard,
    setLoadingSchool,
    setLoadingRole,
    setEnrolledList,
    setUnEnrolledList,
    setLoadingEnrollments,
    setLoadingCreateAccount,
    setFailedCreateAccount,
    setExportFile,
    setExportFileUrl,
    setLoadingExport,
    clearExportFile,
    clearExportFileUrl,
    setTemplateFile,
    setLoadingDownloadTemplate,
    clearTemplateFile,
    setHistoryLogs,
    setLoadingHistoryLogs,
    clearHistoryLogs,
    setDeleteHistoryLogs,
    setLoadingDeleteHistoryLogs,
    clearDeleteHistoryLogs,
    setRetryingTask,
    setLoadingDeleteUser,
    setDeleteUserSuccess,
    setImportProgressSession,
    setDeleteProgressSession,
    setValidationSession,
    setDownloadValidatedExcelFile,
    setValidationPreFilterFailures,
} = accountSlice.actions;

export default accountSlice.reducer;
